import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import {
  Plus, Trash2, DollarSign, Calculator, Settings2, Percent, Layers, BarChart3, RotateCcw
} from "lucide-react";
import { applyMargin, calcItemRaw } from "../../utils/pricing";
import { computeQuoteDiscounts } from "../../lib/quoteDiscounts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Calculate per-item total
const calcItemTotal = (it) => {
  const qty = parseFloat(it.quantity) || 0;
  const price = parseFloat(it.unit_price) || 0;
  const margin = parseFloat(it.margin) || 0;
  const waste = parseFloat(it.waste_factor) || 0;
  const effectiveQty = qty * (1 + waste / 100);
  return effectiveQty * price * (1 + margin / 100);
};

// =================== TEMPLATE-BASED PER-PAGE PRICING ===================

function PerPagePricing({ pricingPages, quote, priceList, updatePageLineItem, togglePageCashDiscount, updatePageTaxRate, onPriceAdjustmentChange, onSetPagePrice, onTogglePageEnabled, onResetMarginsToPriceList }) {
  // selectedOptionIdx = null means "nothing selected" so customer-total math
  // matches the View & Sign rule: no option = no price displayed. Set with a
  // real index only when the admin clicks an option.
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
  // enabledAddons starts EMPTY — nothing is auto-selected. Admin clicks to
  // include an addon. Matches PublicQuoteView.js behavior.
  const [enabledAddons, setEnabledAddons] = useState({});

  // Shared structure raw cost (included in every option's customer-facing total).
  // Uses the same per-item margin baked-in formula as the customer view + option
  // editor (calcItemTotal / calcItemRaw) so the Pricing tab totals match
  // PublicQuoteView, the option editor's "Customer Total", and the PDF.
  const structureRawTotal = (pricingPages || [])
    .filter(p => p.page_type === "structure")
    .reduce((sum, p) => {
      const items = (p.content?.line_items || []).filter(it => it.visible !== false);
      return sum + items.reduce((s, it) => s + calcItemRaw(it), 0);
    }, 0);
  const globalProfitPct = parseFloat(quote?.profit_margin_pct) || 0;
  const globalVendorTaxPct = parseFloat(quote?.vendor_tax_pct) || 0;

  return (
    <div className="space-y-6">
      {/* Structure shared items */}
      {pricingPages.filter(p => p.page_type === "structure").map((page, si) => {
        const c = page.content || {};
        const items = c.line_items || [];
        const structureSub = items.reduce((sum, it) => sum + calcItemTotal(it), 0);
        return (
          <Card key={page.id} className="border-indigo-200" data-testid={`pricing-structure-${si}`}>
            <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">{c.title || "Structure"}</h3>
                <Badge className="bg-white/20 text-white text-[10px]">Shared Items</Badge>
              </div>
              <div className="flex items-center gap-3">
                {onResetMarginsToPriceList && (
                  <button
                    type="button"
                    onClick={() => onResetMarginsToPriceList(page.id)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white/90 hover:text-white text-[11px] font-medium transition-colors"
                    title="Reset every item's margin on this page back to its Price List default"
                    data-testid={`reset-margins-structure-${si}`}
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset margins
                  </button>
                )}
                <p className="text-lg font-bold text-white">${structureSub.toFixed(2)}</p>
              </div>
            </div>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <div key={it.id || idx} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50" data-testid={`structure-item-${si}-${idx}`}>
                    <span className="text-sm flex-1 text-slate-700">{it.name}</span>
                    <Input type="number" value={it.quantity ?? ''} onChange={e => updatePageLineItem(page.id, idx, "quantity", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-16 text-xs text-center" />
                    <span className="text-[10px] text-slate-400">{it.unit || "ea"}</span>
                    <Input type="number" step="0.01" value={it.unit_price ?? ''} onChange={e => updatePageLineItem(page.id, idx, "unit_price", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-20 text-xs text-right" />
                    <div className="flex items-center gap-0.5" title="Per-item margin % — stacked on top of the global Profit Margin">
                      <span className="text-[9px] text-amber-600 font-bold">+</span>
                      <Input
                        type="number"
                        step="1"
                        value={it.margin ?? ''}
                        onChange={e => updatePageLineItem(page.id, idx, "margin", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        placeholder="0"
                        className={`h-7 w-12 text-xs text-right ${parseFloat(it.margin) > 0 ? "border-amber-300 text-amber-700 font-semibold" : "text-slate-400"}`}
                        data-testid={`structure-item-margin-${si}-${idx}`}
                      />
                      <span className="text-[9px] text-amber-600">%</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-700 w-20 text-right">${calcItemTotal(it).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 bg-indigo-50 flex justify-between text-sm font-bold">
                <span className="text-indigo-700">Shared subtotal (included in every option below)</span>
                <span className="text-indigo-700">${structureSub.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Option and Add-on pages */}
      {pricingPages.filter(p => p.page_type === "option" || p.page_type === "addon").map((page, pageIdx) => {
        const c = page.content || {};
        const items = c.line_items || [];
        const groups = c.structures || [];
        const isOption = page.page_type === "option";
        const taxRate = parseFloat(c.tax_rate) || 0;
        const hasItems = items.length > 0;
        const manualPrice = parseFloat(c.base_price || c.manual_price) || 0;
        const itemsSum = items.reduce((sum, it) => sum + calcItemTotal(it), 0);
        // Unified customer-view formula (mirrors PublicQuoteView /
        // calcFullOptionPrice). Options layer global profit margin + vendor
        // tax + shared structure cost + per-option markup/adj. Add-ons use
        // their own tax_rate.
        const hasManualOverride = manualPrice > 0;
        const optMkp = parseFloat(c.price_markup_pct) || 0;
        const optAdj = parseFloat(c.price_adjustment) || 0;
        let pageSubtotal = 0;
        let pageTax = 0;
        if (isOption) {
          if (hasManualOverride) {
            pageSubtotal = manualPrice + (manualPrice * optMkp / 100) + optAdj;
            pageTax = 0;
          } else {
            const totalRaw = itemsSum + structureRawTotal;
            const withMargin = applyMargin(totalRaw, globalProfitPct);
            const rawTax = totalRaw * (globalVendorTaxPct / 100);
            const mkpMult = 1 + optMkp / 100;
            pageSubtotal = withMargin * mkpMult + optAdj;
            pageTax = rawTax * mkpMult;
          }
        } else {
          // addon
          if (hasManualOverride) {
            pageSubtotal = manualPrice + (manualPrice * optMkp / 100) + optAdj;
            pageTax = 0;
          } else {
            const sub = itemsSum;
            const rawTax = sub * (taxRate / 100);
            const mkpMult = 1 + optMkp / 100;
            pageSubtotal = sub * mkpMult + optAdj;
            pageTax = rawTax * mkpMult;
          }
        }
        const pageTotal = pageSubtotal + pageTax;
        const pageCashPrice = pageTotal * 0.98;
        const visibleItems = items.filter(it => it.visible !== false);
        const visibleSubtotal = visibleItems.reduce((sum, it) => sum + calcItemTotal(it), 0);
        const ungroupedItems = items.filter(it => !it.structure_group);

        // Simplified card for pages without line items
        if (!hasItems) {
          return (
            <Card key={page.id} className={`overflow-hidden ${isOption ? "border-[#0F4C81]/30" : "border-emerald-300/50"}`} data-testid={`pricing-page-${pageIdx}`}>
              <div className={`px-5 py-3 flex items-center justify-between ${isOption ? "bg-[#0F4C81]" : "bg-emerald-600"}`}>
                <div className="flex items-center gap-3">
                  <Badge className="bg-white/20 text-white text-[10px] uppercase">{page.page_type}</Badge>
                  <h3 className="text-lg font-bold text-white">{c.option_name || c.addon_name || page.title}</h3>
                  {c.option_tag && <Badge className="bg-white/20 text-white border-white/30 text-xs">{c.option_tag}</Badge>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-white">${pageTotal.toFixed(2)}</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-wider">Total</p>
                </div>
              </div>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-semibold text-slate-700 whitespace-nowrap">Set Price</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400">$</span>
                    <Input
                      type="number" step="1" min="0"
                      value={parseFloat(c.base_price || c.manual_price) || ""}
                      onChange={e => onSetPagePrice(page.id, parseFloat(e.target.value) || 0)}
                      className="h-9 w-32 text-sm"
                      placeholder="0.00"
                      data-testid={`page-price-input-${pageIdx}`}
                    />
                    {hasManualOverride && (
                      <button
                        type="button"
                        onClick={() => onSetPagePrice(page.id, 0)}
                        className="ml-1 h-9 px-2 rounded-md border border-amber-300 bg-amber-50 text-[11px] text-amber-700 hover:bg-amber-100 whitespace-nowrap"
                        title="Clear Set Price so line items + global margin recompute this option"
                        data-testid={`page-clear-set-price-${pageIdx}`}
                      >Use line items instead</button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Label className="text-xs text-slate-500">Tax</Label>
                    <Input type="number" step="0.1" value={taxRate || ''} onChange={e => updatePageTaxRate(page.id, e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-9 w-16 text-xs text-center" />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                </div>
                {hasManualOverride && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800 leading-snug">
                    <strong>Set Price is locked in.</strong> Global margin and tax are ignored on this option. Click &ldquo;Use line items instead&rdquo; above to unlock and let margin flow through.
                  </div>
                )}
                <div className="flex justify-between items-center text-sm pt-1 border-t">
                  <label className="flex items-center gap-2 text-slate-500">
                    <Switch checked={c.show_cash_discount !== false} onCheckedChange={v => togglePageCashDiscount(page.id, v)} className="scale-75" />
                    Cash / Check Discount (2%)
                  </label>
                  {c.show_cash_discount !== false && <span className="font-bold text-emerald-600">${pageCashPrice.toFixed(2)}</span>}
                </div>
              </CardContent>
            </Card>
          );
        }

        const renderPageItem = (it, idx) => {
          const itemTotal = calcItemTotal(it);
          return (
            <tr key={it.id || idx} className={`border-b border-slate-100 hover:bg-slate-50 ${it.visible === false ? "opacity-40" : ""}`} data-testid={`page-line-item-${pageIdx}-${idx}`}>
              <td className="py-2 px-2">
                <div className="flex items-center gap-2">
                  {it.visible === false && <Badge variant="outline" className="text-[8px] px-1 h-4">Hidden</Badge>}
                  <span className="text-sm text-slate-700">{it.name}</span>
                  {it.measurement_key && <Badge variant="outline" className="text-[8px] px-1 h-4 text-blue-600">{it.measurement_key}</Badge>}
                </div>
              </td>
              <td className="py-2 px-1 text-center">
                <Input type="number" value={it.quantity ?? ''} onChange={e => updatePageLineItem(page.id, idx, "quantity", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-16 text-xs text-center" />
              </td>
              <td className="py-2 px-1 text-xs text-slate-400">{it.unit || "ea"}</td>
              <td className="py-2 px-1 text-right">
                <Input type="number" step="0.01" value={it.unit_price ?? ''} onChange={e => updatePageLineItem(page.id, idx, "unit_price", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-20 text-xs text-right" />
              </td>
              <td className="py-2 px-1 text-right">
                <div className="flex items-center justify-end gap-0.5" title="Per-item margin %">
                  <Input
                    type="number"
                    step="1"
                    value={it.margin ?? ''}
                    onChange={e => updatePageLineItem(page.id, idx, "margin", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                    placeholder="0"
                    className={`h-7 w-12 text-xs text-right ${parseFloat(it.margin) > 0 ? "border-amber-300 text-amber-700 font-semibold" : "text-slate-400"}`}
                    data-testid={`page-item-margin-${pageIdx}-${idx}`}
                  />
                  <span className="text-[9px] text-amber-600">%</span>
                </div>
              </td>
              <td className="py-2 px-2 text-right font-semibold text-sm text-slate-800">${itemTotal.toFixed(2)}</td>
            </tr>
          );
        };

        return (
          <Card key={page.id} className={`overflow-hidden ${isOption ? "border-[#0F4C81]/30" : "border-emerald-300/50"}`} data-testid={`pricing-page-${pageIdx}`}>
            <div className={`px-5 py-3 flex items-center justify-between ${isOption ? "bg-[#0F4C81]" : "bg-emerald-600"}`}>
              <div className="flex items-center gap-3">
                <Badge className={`${isOption ? "bg-white/20 text-white" : "bg-white/20 text-white"} text-[10px] uppercase`}>{page.page_type}</Badge>
                <h3 className="text-lg font-bold text-white">{c.option_name || c.addon_name || page.title}</h3>
                {c.option_tag && <Badge className="bg-white/20 text-white border-white/30 text-xs">{c.option_tag}</Badge>}
              </div>
              <div className="flex items-center gap-3">
                {onResetMarginsToPriceList && items.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onResetMarginsToPriceList(page.id)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white/90 hover:text-white text-[11px] font-medium transition-colors"
                    title="Reset every item's margin on this page back to its Price List default"
                    data-testid={`reset-margins-page-${pageIdx}`}
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset margins
                  </button>
                )}
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-white">${pageTotal.toFixed(2)}</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-wider">{isOption ? "Customer Price" : "Raw Cost"}</p>
                </div>
              </div>
            </div>

            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-2 px-2 text-xs font-bold text-slate-500 uppercase">Item</th>
                    <th className="text-center py-2 px-1 text-xs font-bold text-slate-500 uppercase w-16">Qty</th>
                    <th className="py-2 px-1 text-xs font-bold text-slate-500 uppercase w-8">Unit</th>
                    <th className="text-right py-2 px-1 text-xs font-bold text-slate-500 uppercase w-20">Price</th>
                    <th className="text-right py-2 px-1 text-xs font-bold text-slate-500 uppercase w-12">Mgn</th>
                    <th className="text-right py-2 px-2 text-xs font-bold text-slate-500 uppercase w-24">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const gItems = items.map((it, idx) => ({ it, idx })).filter(({ it }) => it.structure_group === g.id);
                    const groupTotal = gItems.reduce((sum, { it }) => sum + calcItemTotal(it), 0);
                    if (gItems.length === 0) return null;
                    return (
                      <React.Fragment key={g.id}>
                        <tr className={`${g.shared ? "bg-blue-50" : "bg-slate-50"}`}>
                          <td colSpan="5" className="py-1.5 px-2">
                            <div className="flex items-center gap-2">
                              <Layers className="w-3 h-3 text-slate-400" />
                              <span className="text-xs font-bold uppercase text-slate-500 tracking-wide">{g.name}</span>
                              {g.shared && <Badge className="text-[8px] bg-blue-100 text-blue-600">Shared</Badge>}
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right text-xs font-bold text-slate-500">${groupTotal.toFixed(2)}</td>
                        </tr>
                        {gItems.map(({ it, idx }) => renderPageItem(it, idx))}
                      </React.Fragment>
                    );
                  })}
                  {ungroupedItems.length > 0 && (
                    <>
                      {groups.length > 0 && (
                        <tr className="bg-slate-50">
                          <td colSpan="6" className="py-1 px-2 text-[10px] uppercase font-bold text-slate-300 tracking-wider">Ungrouped</td>
                        </tr>
                      )}
                      {items.map((it, idx) => !it.structure_group ? renderPageItem(it, idx) : null)}
                    </>
                  )}
                </tbody>
              </table>

              <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-2">
                {hasManualOverride && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-center justify-between gap-3 text-[11px] text-amber-800 leading-snug">
                    <span><strong>Set Price is locked in.</strong> Global margin, tax and line items are ignored on this option.</span>
                    <button
                      type="button"
                      onClick={() => onSetPagePrice(page.id, 0)}
                      className="h-7 px-2 rounded-md border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 whitespace-nowrap"
                      data-testid={`page-clear-set-price-inline-${pageIdx}`}
                    >Use line items instead</button>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">
                    {hasManualOverride ? "Set price" : `Subtotal (${items.length} items)`}
                  </span>
                  <span className="font-semibold" data-testid={`page-subtotal-${pageIdx}`}>${pageSubtotal.toFixed(2)}</span>
                </div>
                {!hasManualOverride && visibleSubtotal !== pageSubtotal && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Customer Visible ({visibleItems.length} items)</span>
                    <span className="text-slate-500">${visibleSubtotal.toFixed(2)}</span>
                  </div>
                )}
                {!hasManualOverride && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 flex items-center gap-2">Tax
                      <Input type="number" step="0.1" value={taxRate || ''} onChange={e => updatePageTaxRate(page.id, e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-6 w-14 text-xs text-center inline-block" />%
                    </span>
                    <span className="font-medium">${pageTax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span className={isOption ? "text-[#0F4C81]" : "text-emerald-700"}>Total</span>
                  <span className={isOption ? "text-[#0F4C81]" : "text-emerald-700"} data-testid={`page-total-${pageIdx}`}>${pageTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm pt-1">
                  <label className="flex items-center gap-2 text-slate-500">
                    <Switch checked={c.show_cash_discount !== false} onCheckedChange={v => togglePageCashDiscount(page.id, v)} className="scale-75" />
                    Cash / Check Discount (2%)
                  </label>
                  {c.show_cash_discount !== false && <span className="font-bold text-emerald-600" data-testid={`page-cash-${pageIdx}`}>${pageCashPrice.toFixed(2)}</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* =================== PRICING SUMMARY =================== */}
      {(() => {
        const priceMap = {};
        (priceList || []).forEach(p => { priceMap[p.id] = p; });
        const getCategory = (item) => {
          if (item.price_list_id && priceMap[item.price_list_id]) return priceMap[item.price_list_id].category || "Material";
          const name = (item.name || "").toLowerCase();
          if (name.includes("labor") || name.includes("install") || name.includes("tear")) return "Labor";
          return "Material";
        };
        const calcCost = (it) => {
          const qty = parseFloat(it.quantity) || 0;
          const price = parseFloat(it.unit_price) || 0;
          const waste = parseFloat(it.waste_factor) || 0;
          return qty * (1 + waste / 100) * price;
        };
        const structurePages = pricingPages.filter(p => p.page_type === "structure");
        const optionPages = pricingPages.filter(p => p.page_type === "option");
        const addonPages = pricingPages.filter(p => p.page_type === "addon");

        // Shared structure raw cost (added to every option, internal only).
        // Uses calcItemRaw (includes per-item margins) to match customer view +
        // option editor "Customer Total" exactly.
        const structureRawTotal = structurePages.reduce((sum, p) => {
          const items = (p.content?.line_items || []).filter(it => it.visible !== false);
          return sum + items.reduce((s, it) => s + calcItemRaw(it), 0);
        }, 0);

        // ---- Customer-view pricing formula (mirrors PublicQuoteView /
        // calcFullOptionPrice / services/quote_pricing.py). Each option = own
        // line items + shared structure + global profit margin + vendor tax +
        // per-option markup% + per-option $ adjustment. Manual "Sells For"
        // overrides everything except per-option markup/adjustment. ----
        const globalProfitPct = parseFloat(quote.profit_margin_pct) || 0;
        const globalVendorTaxPct = parseFloat(quote.vendor_tax_pct) || 0;

        const getPageTotals = (page) => {
          const c = page.content || {};
          const items = (c.line_items || []).filter(it => it.visible !== false);
          const taxRate = parseFloat(c.tax_rate) || 0;
          const manualPrice = parseFloat(c.base_price || c.manual_price) || 0;
          const isOption = page.page_type === "option";
          const isAddon = page.page_type === "addon";
          const isStructure = page.page_type === "structure";

          let mc = 0, lc = 0;
          items.forEach(it => {
            const cost = calcCost(it);
            if (getCategory(it) === "Labor") lc += cost; else mc += cost;
          });

          // Optional cost override on the page
          const costOverride = parseFloat(c.cost_override);
          if (!isNaN(costOverride) && costOverride > 0) {
            const totalItemsCost = mc + lc;
            if (totalItemsCost > 0) {
              const ratio = costOverride / totalItemsCost;
              mc = mc * ratio;
              lc = lc * ratio;
            } else {
              lc = costOverride;
              mc = 0;
            }
          }

          // Per-option markup and adjustment
          const optMkp = parseFloat(c.price_markup_pct) || 0;
          const optAdj = parseFloat(c.price_adjustment) || 0;

          // Revenue = customer-facing total for this page
          // rev = pre-tax customer-facing subtotal (incl global margin + per-option markup/adj)
          // tax = vendor/page tax applied with the same per-option markup multiplier
          // total shown = rev + tax (matches PublicQuoteView's oTotal exactly)
          let rev = 0;
          let tax = 0;
          if (isOption) {
            const optionItemsRaw = items.reduce((s, it) => s + calcItemRaw(it), 0);
            if (manualPrice > 0) {
              // Manual sells-for: it IS the final sells-for, no margin/tax stack
              const base = manualPrice;
              rev = base + (base * optMkp / 100) + optAdj;
              tax = 0;
            } else {
              const totalRaw = optionItemsRaw + structureRawTotal;
              const withMargin = applyMargin(totalRaw, globalProfitPct);
              const rawTax = totalRaw * (globalVendorTaxPct / 100);
              const mkpMult = 1 + optMkp / 100;
              rev = withMargin * mkpMult + optAdj;
              tax = rawTax * mkpMult;
            }
          } else if (isAddon) {
            const addonItemsRev = items.reduce((s, it) => s + calcItemRaw(it), 0);
            if (manualPrice > 0) {
              rev = manualPrice + (manualPrice * optMkp / 100) + optAdj;
              tax = 0;
            } else {
              const rawTax = addonItemsRev * (taxRate / 100);
              const mkpMult = 1 + optMkp / 100;
              rev = addonItemsRev * mkpMult + optAdj;
              tax = rawTax * mkpMult;
            }
          } else if (isStructure) {
            // Structure pages are internal cost-only — never shown as revenue.
            rev = 0;
            tax = 0;
          }

          return { mc, lc, rev, tax, cost: mc + lc };
        };

        // Structure totals (always included)
        // Structure costs (always included in material/labor cost breakdown).
        // Revenue is intentionally 0 — already baked into each option's rev
        // via shared structureRawTotal + global margin formula above.
        let structureMat = 0, structureLab = 0;
        structurePages.forEach(p => {
          const t = getPageTotals(p);
          structureMat += t.mc; structureLab += t.lc;
        });

        // Per-option totals
        const optionTotals = optionPages.map(p => {
          const t = getPageTotals(p);
          const c = p.content || {};
          return { page: p, name: c.option_name || p.title || "Option", ...t };
        });

        // Per-addon totals
        const addonTotals = addonPages.map(p => {
          const t = getPageTotals(p);
          const c = p.content || {};
          return { page: p, name: c.addon_name || p.title || "Add-on", ...t };
        });

        // Use selected option (null until admin explicitly clicks one)
        const selOpt = selectedOptionIdx !== null ? optionTotals[selectedOptionIdx] : null;
        const optMat = selOpt?.mc || 0, optLab = selOpt?.lc || 0, optRev = selOpt?.rev || 0, optTax = selOpt?.tax || 0;

        // Sum only enabled addons (default = nothing enabled)
        let addonRev = 0, addonMat = 0, addonLab = 0, addonTax = 0;
        addonTotals.forEach(a => {
          if (enabledAddons[a.page.id]) {
            addonRev += a.rev; addonMat += a.mc; addonLab += a.lc; addonTax += a.tax;
          }
        });

        const baseRevenue = optRev + addonRev;
        const totalTax = optTax + addonTax;
        const customerTotal = baseRevenue + totalTax;
        const materialCost = structureMat + optMat + addonMat;
        const laborCost = structureLab + optLab + addonLab;
        const totalCost = materialCost + laborCost;
        const markupPct = parseFloat(quote.price_markup_pct) || 0;
        const markupAmt = customerTotal * (markupPct / 100);
        const priceAdj = parseFloat(quote.price_adjustment) || 0;
        const revenueBeforeDiscount = customerTotal + markupAmt + priceAdj;
        const { totalRemoved: quoteDiscount, rows: discountRows } = computeQuoteDiscounts(revenueBeforeDiscount, quote);
        const finalRevenue = Math.max(0, revenueBeforeDiscount - quoteDiscount);
        const profit = finalRevenue - totalCost - totalTax;
        const marginPct = baseRevenue > 0 ? (profit / baseRevenue * 100) : 0;
        
        return (
          <>
            {/* Page Revenue Breakdown */}
            <Card className="bg-gradient-to-br from-[#0F4C81] to-[#0D4270] text-white" data-testid="pricing-summary-card">
              <CardContent className="p-6">
                <h3 className="text-sm uppercase tracking-widest text-blue-200 font-bold mb-4">Pricing Summary</h3>

                {/* Structure pages are internal cost-tracking only; their
                    materials/labor flow into profit math but the customer-
                    facing total assumes the admin already rolled those costs
                    into each option price. Hidden here so the summary mirrors
                    what the customer actually sees. */}

                {/* Options with radio selection */}
                {optionPages.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-1">Options (Select One)</p>
                    {optionTotals.map((opt, oi) => {
                      const isSelected = oi === selectedOptionIdx;
                      return (
                        <button
                          key={opt.page.id}
                          type="button"
                          onClick={() => setSelectedOptionIdx(oi)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-left ${isSelected ? "bg-white/15" : "hover:bg-white/5"}`}
                          data-testid={`select-option-${oi}`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-white" : "border-blue-300"}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className={`text-sm flex-1 ${isSelected ? "text-white font-semibold" : "text-blue-200"}`}>{opt.name}</span>
                          <span className={`font-semibold text-sm ${isSelected ? "text-white" : "text-blue-200"}`}>${(opt.rev + opt.tax).toFixed(2)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Addons */}
                {addonPages.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-1">Add-ons (Toggle On/Off)</p>
                    {addonTotals.map((addon) => {
                      const isOn = !!enabledAddons[addon.page.id];
                      return (
                        <button
                          key={addon.page.id}
                          type="button"
                          onClick={() => setEnabledAddons(prev => ({ ...prev, [addon.page.id]: !prev[addon.page.id] }))}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-left ${isOn ? "bg-white/15" : "hover:bg-white/5"}`}
                          data-testid={`toggle-addon-${addon.page.id}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isOn ? "border-emerald-400 bg-emerald-400" : "border-blue-300"}`}>
                            {isOn && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <span className={`text-sm flex-1 ${isOn ? "text-white font-semibold" : "text-blue-300"}`}>{addon.name}</span>
                          <span className={`font-semibold text-sm ${isOn ? "text-white" : "text-blue-300"}`}>${(addon.rev + addon.tax).toFixed(2)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="border-t border-white/10 pt-3 mt-2 space-y-1">
                  <div className="flex justify-between text-blue-200">
                    <span className="text-xs">Subtotal</span>
                    <span className="text-xs font-semibold">${baseRevenue.toFixed(2)}</span>
                  </div>
                  {totalTax > 0 && (
                    <div className="flex justify-between text-blue-200">
                      <span className="text-xs">Tax</span>
                      <span className="text-xs font-semibold">${totalTax.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-blue-100">
                    <span className="text-sm font-semibold">Base Total</span>
                    <span className="font-bold">${customerTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Adjustments */}
                <div className="border-t border-white/10 mt-4 pt-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Adjustments</p>
                  <div className="flex items-center gap-3">
                    <Label className="text-xs text-blue-200 whitespace-nowrap w-24">Markup</Label>
                    <Input
                      type="number" step="0.5" min="-100"
                      value={quote.price_markup_pct || ""}
                      onChange={e => onPriceAdjustmentChange(null, null, parseFloat(e.target.value) || 0)}
                      className="h-8 w-20 text-xs text-center bg-white/10 border-white/20 text-white placeholder:text-blue-300"
                      placeholder="0"
                      data-testid="price-markup-pct-input"
                    />
                    <span className="text-xs text-blue-300">%</span>
                    {markupAmt !== 0 && <span className={`text-xs font-semibold ${markupAmt > 0 ? "text-emerald-300" : "text-red-300"}`}>{markupAmt > 0 ? "+" : ""}${markupAmt.toFixed(2)}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className="text-xs text-blue-200 whitespace-nowrap w-24">Flat Adjust</Label>
                    <span className="text-xs text-blue-300">$</span>
                    <Input
                      type="number" step="1"
                      value={quote.price_adjustment || ""}
                      onChange={e => onPriceAdjustmentChange(parseFloat(e.target.value) || 0)}
                      className="h-8 w-24 text-xs text-center bg-white/10 border-white/20 text-white placeholder:text-blue-300"
                      placeholder="0.00"
                      data-testid="price-adjustment-input"
                    />
                    <Input
                      type="text"
                      value={quote.price_adjustment_label || ""}
                      onChange={e => onPriceAdjustmentChange(quote.price_adjustment || 0, e.target.value)}
                      className="h-8 flex-1 text-xs bg-white/10 border-white/20 text-white placeholder:text-blue-300"
                      placeholder="Label (optional)"
                      data-testid="price-adjustment-label"
                    />
                  </div>
                </div>

                {/* Quote-level discounts (multi). Only rendered when at least
                    one row has a positive value — matches customer view & PDF. */}
                {discountRows.length > 0 && (
                  <div className="border-t border-white/10 mt-4 pt-3 space-y-1">
                    <p className="text-[10px] uppercase tracking-widest text-rose-200 font-bold">Discounts</p>
                    {discountRows.map((d, i) => (
                      <div key={i} className="flex justify-between text-rose-200">
                        <span className="text-xs">{d.label}</span>
                        <span className="text-xs font-semibold" data-testid={`pricing-discount-${i}`}>− ${d.removed.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Grand Total */}
                <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-end">
                  <span className="text-lg font-bold">Customer Total</span>
                  <span className="text-3xl font-extrabold" data-testid="quote-grand-total">${finalRevenue.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Selected Option Breakdown */}
            {selOpt && (
              <Card className="border-slate-200" data-testid="option-breakdown-card">
                <CardContent className="p-5">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                    Breakdown: {selOpt.name}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Costs by Type</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-blue-600">Material</span>
                          <span className="font-semibold">${(structureMat + optMat).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-orange-600">Labor</span>
                          <span className="font-semibold">${(structureLab + optLab).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Price Breakdown</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Option + Structure</span>
                          <span className="font-semibold">${optRev.toFixed(2)}</span>
                        </div>
                        {optTax > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-600">Tax</span>
                            <span className="font-semibold">${optTax.toFixed(2)}</span>
                          </div>
                        )}
                        {quoteDiscount > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-rose-600">Discounts</span>
                            <span className="font-semibold text-rose-600">− ${quoteDiscount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-xs border-t pt-1">
                          <span className="font-bold text-slate-700">Subtotal</span>
                          <span className="font-bold text-slate-700">${Math.max(0, optRev + optTax - quoteDiscount).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Margin</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Cost</span>
                          <span className="font-semibold">${(structureMat + structureLab + optMat + optLab).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Revenue</span>
                          <span className="font-semibold">${Math.max(0, optRev + optTax - quoteDiscount).toFixed(2)}</span>
                        </div>
                        {(() => {
                          const optCost = structureMat + structureLab + optMat + optLab;
                          const optRevTot = Math.max(0, optRev + optTax - quoteDiscount);
                          const optProfit = optRevTot - optCost - optTax;
                          const optMargin = optRevTot > 0 ? (optProfit / optRevTot * 100) : 0;
                          const tone = optMargin >= 30 ? "text-emerald-600" : optMargin >= 15 ? "text-amber-600" : "text-red-600";
                          return (
                            <>
                              <div className={`flex justify-between text-xs border-t pt-1 ${tone}`}>
                                <span className="font-bold">Profit</span>
                                <span className="font-bold" data-testid={`option-profit-${selOpt?.id || "sel"}`}>${optProfit.toFixed(2)}</span>
                              </div>
                              <div className={`flex justify-between text-xs ${tone}`}>
                                <span className="font-bold">Margin</span>
                                <span className="font-bold">{optMargin.toFixed(1)}%</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cost & Profit Breakdown */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white" data-testid="profit-breakdown-card">
              <CardContent className="p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Your Numbers</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-blue-300">Materials</span>
                    <span className="text-sm font-semibold">${materialCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-orange-300">Labor</span>
                    <span className="text-sm font-semibold">${laborCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Your Total Cost</span>
                    <span className="text-sm font-bold">${totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Customer Pays</span>
                    <span className="text-sm font-bold">${finalRevenue.toFixed(2)}</span>
                  </div>
                  {totalTax > 0 && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-xs text-yellow-300">Tax (pass-through)</span>
                      <span className="text-sm font-semibold text-yellow-300">${totalTax.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-white/10 pt-3 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Your Profit</p>
                    <p className={`text-2xl font-extrabold ${profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>${profit.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Profit %</p>
                    <p className={`text-2xl font-extrabold ${profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{marginPct.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        );
      })()}
    </div>
  );
}

// =================== FLAT LINE ITEMS PRICING ===================

function FlatPricing({
  quote, baseItems, addonItems, selectedAddons, toggleAddon,
  handleRemoveLineItem, setNewLineItem, setShowLineItemModal,
  markupSettings, setMarkupSettings, showMarkupSettings, setShowMarkupSettings,
  handleCalculate, isNew
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Line Items</CardTitle>
              <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => { setNewLineItem(prev => ({ ...prev, is_addon: false })); setShowLineItemModal(true); }} data-testid="add-line-item-btn">
                <Plus className="w-5 h-5 mr-2" />Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {baseItems.length > 0 ? (
              <div className="space-y-3">
                {baseItems.map(item => (
                  <div key={item.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg group">
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">{item.name}</p>
                      {item.description && <p className="text-sm text-slate-500">{item.description}</p>}
                      <div className="flex gap-4 mt-2 text-sm text-slate-600">
                        <span>Qty: {item.quantity} {item.unit}</span>
                        <span>@ ${item.unit_price}/{item.unit || 'ea'}</span>
                        {item.markup > 0 && <span className="text-emerald-600">+{item.markup}%</span>}
                      </div>
                    </div>
                    <p className="font-semibold text-slate-800">${((item.quantity || 1) * (item.unit_price || 0) * (1 + (item.markup || 0) / 100) - (item.discount || 0)).toFixed(2)}</p>
                    <Button variant="ghost" size="sm" className="text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100" onClick={() => handleRemoveLineItem(item.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-3">No line items</p>
                <Button onClick={() => setShowLineItemModal(true)}><Plus className="w-5 h-5 mr-2" />Add from Price List</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Optional Add-ons</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Customer can select these on the signature page</p>
              </div>
              <Button variant="outline" onClick={() => { setNewLineItem(prev => ({ ...prev, is_addon: true })); setShowLineItemModal(true); }}>
                <Plus className="w-5 h-5 mr-2" />Add-on
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {addonItems.length > 0 ? (
              <div className="space-y-3">
                {addonItems.map(item => (
                  <div key={item.id} className="flex items-center gap-4 p-4 rounded-lg border border-dashed border-slate-300 group">
                    <input type="checkbox" checked={!!selectedAddons[item.id]} onChange={() => toggleAddon(item.id)} className="w-5 h-5 accent-[#0F4C81]" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">{item.name}</p>
                      {item.description && <p className="text-sm text-slate-500">{item.description}</p>}
                      <span className="text-sm text-slate-500">Qty: {item.quantity} {item.unit} @ ${item.unit_price}</span>
                    </div>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">Optional</Badge>
                    <p className="font-semibold text-slate-800">${((item.quantity || 1) * (item.unit_price || 0) * (1 + (item.markup || 0) / 100)).toFixed(2)}</p>
                    <Button variant="ghost" size="sm" className="text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100" onClick={() => handleRemoveLineItem(item.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-slate-400 text-sm">No add-ons. Add optional items the customer can choose.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Totals</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowMarkupSettings(!showMarkupSettings)} data-testid="markup-settings-btn">
                <Settings2 className="w-4 h-4 mr-1" />Settings
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showMarkupSettings && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-3 border border-slate-200">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Auto Markup</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={markupSettings.auto_markup_enabled} onChange={(e) => setMarkupSettings(prev => ({ ...prev, auto_markup_enabled: e.target.checked }))} className="w-4 h-4 accent-[#0F4C81]" />
                    <span className="text-sm">{markupSettings.auto_markup_enabled ? "On" : "Off"}</span>
                  </label>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Default Markup %</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.1" value={markupSettings.default_markup ?? ''} onChange={(e) => setMarkupSettings(prev => ({ ...prev, default_markup: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-9" />
                    <Percent className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Tax Rate %</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.1" value={markupSettings.tax_rate ?? ''} onChange={(e) => setMarkupSettings(prev => ({ ...prev, tax_rate: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-9" data-testid="tax-rate-input" />
                    <Percent className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between text-slate-600">
              <span>Subtotal</span>
              <span className="font-medium" data-testid="quote-subtotal">${quote.subtotal.toLocaleString()}</span>
            </div>
            {markupSettings.auto_markup_enabled && quote.markup_amount > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Markup ({markupSettings.default_markup}%)</span>
                <span className="font-medium text-emerald-600">+${quote.markup_amount.toLocaleString()}</span>
              </div>
            )}
            {markupSettings.tax_rate > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Tax ({markupSettings.tax_rate}%)</span>
                <span className="font-medium">${quote.tax_amount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between">
                <span className="text-lg font-semibold text-slate-800">Total</span>
                <span className="text-2xl font-bold text-[#0F4C81]" data-testid="quote-total">${quote.total.toLocaleString()}</span>
              </div>
            </div>
            {!isNew && (
              <Button className="w-full mt-4" variant="outline" onClick={handleCalculate}>
                <Calculator className="w-4 h-4 mr-2" />Server Recalculate
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// =================== DEPOSIT CARD ===================
// Editable per-quote deposit: on/off + percent or flat $ amount.
// Persists via PATCH /api/quotes/{id} whitelisted fields.
function DepositCard({ quote }) {
  const templateDefaultPct = 50;
  const initialRequired = quote?.deposit_required;
  const initialType = quote?.deposit_type || "percent";
  const initialPct = quote?.deposit_percent ?? templateDefaultPct;
  const initialFlat = quote?.deposit_flat_amount ?? 0;

  const [required, setRequired] = useState(initialRequired !== false);
  const [type, setType] = useState(initialType);
  const [pct, setPct] = useState(initialPct);
  const [flat, setFlat] = useState(initialFlat);
  const [saving, setSaving] = useState(false);

  const total = parseFloat(quote?.total || 0) || 0;
  const depositAmount = !required
    ? 0
    : type === "flat"
      ? Math.round((parseFloat(flat) || 0) * 100) / 100
      : Math.round(total * (parseFloat(pct) || 0) / 100 * 100) / 100;

  const patch = async (payload) => {
    if (!quote?.id) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/quotes/${quote.id}`, payload);
    } catch {
      toast.error("Could not save deposit");
    } finally {
      setSaving(false);
    }
  };

  const toggleRequired = (checked) => {
    setRequired(checked);
    patch({ deposit_required: checked });
  };

  const changeType = (nextType) => {
    setType(nextType);
    patch({ deposit_type: nextType });
  };

  const changePct = (val) => {
    const n = Math.max(0, Math.min(100, parseFloat(val) || 0));
    setPct(n);
    patch({ deposit_percent: n, deposit_type: "percent" });
    if (type !== "percent") setType("percent");
  };

  const changeFlat = (val) => {
    const n = Math.max(0, parseFloat(val) || 0);
    setFlat(n);
    patch({ deposit_flat_amount: n, deposit_type: "flat" });
    if (type !== "flat") setType("flat");
  };

  return (
    <Card className={`border-l-4 ${required ? "border-l-[#0F4C81]" : "border-l-slate-300"}`}>
      <CardContent className="p-4">
        {/* Top row — title + on/off toggle */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <DollarSign className={`w-4 h-4 ${required ? "text-[#0F4C81]" : "text-slate-400"}`} />
            <h3 className="font-semibold text-slate-800 text-sm">Deposit Required</h3>
            {saving && <span className="text-[10px] text-slate-400">saving…</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${required ? "text-[#0F4C81] font-semibold" : "text-slate-400"}`}>{required ? "ON" : "OFF"}</span>
            <Switch checked={required} onCheckedChange={toggleRequired} data-testid="quote-deposit-toggle" />
          </div>
        </div>

        {required && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Percent / Flat mode picker */}
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 self-start">
              <button
                type="button"
                onClick={() => changeType("percent")}
                className={`px-3 py-1.5 text-xs font-medium rounded ${type === "percent" ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:text-slate-900"}`}
                data-testid="quote-deposit-type-percent"
              >Percent %</button>
              <button
                type="button"
                onClick={() => changeType("flat")}
                className={`px-3 py-1.5 text-xs font-medium rounded ${type === "flat" ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:text-slate-900"}`}
                data-testid="quote-deposit-type-flat"
              >Flat $</button>
            </div>

            {/* Value input */}
            {type === "percent" ? (
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" max="100" step="1"
                  value={pct}
                  onChange={(e) => changePct(e.target.value)}
                  className="w-20 h-10 text-center text-sm font-semibold"
                  data-testid="quote-deposit-pct"
                />
                <span className="text-sm text-slate-600 font-medium">%</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-sm text-slate-600 font-medium">$</span>
                <Input
                  type="number" min="0" step="1"
                  value={flat}
                  onChange={(e) => changeFlat(e.target.value)}
                  placeholder="1500"
                  className="w-28 h-10 text-sm font-semibold"
                  data-testid="quote-deposit-flat"
                />
              </div>
            )}

            {/* Live-computed amount */}
            <div className="text-right sm:ml-auto">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Deposit amount</p>
              <p className="text-lg font-bold text-[#0F4C81]" data-testid="quote-deposit-amount">${depositAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
          </div>
        )}
        {!required && (
          <p className="text-xs text-slate-500">No deposit — this quote will not auto-create a deposit invoice on signing.</p>
        )}
      </CardContent>
    </Card>
  );
}


// =================== MAIN PRICING TAB EXPORT ===================

export function PricingTab({
  hasPagePricing, pricingPages, quote, priceList,
  updatePageLineItem, togglePageCashDiscount, updatePageTaxRate,
  onPriceAdjustmentChange, onSetPagePrice, onResetMarginsToPriceList,
  baseItems, addonItems, selectedAddons, toggleAddon,
  handleRemoveLineItem, setNewLineItem, setShowLineItemModal,
  markupSettings, setMarkupSettings, showMarkupSettings, setShowMarkupSettings,
  handleCalculate, isNew
}) {
  return (
    <div className="space-y-4">
      <DepositCard quote={quote} />
      {hasPagePricing ? (
        <PerPagePricing
          pricingPages={pricingPages}
          quote={quote}
          priceList={priceList}
          updatePageLineItem={updatePageLineItem}
          togglePageCashDiscount={togglePageCashDiscount}
          updatePageTaxRate={updatePageTaxRate}
          onPriceAdjustmentChange={onPriceAdjustmentChange}
          onSetPagePrice={onSetPagePrice}
          onResetMarginsToPriceList={onResetMarginsToPriceList}
        />
      ) : (
        <FlatPricing
          quote={quote}
          baseItems={baseItems}
          addonItems={addonItems}
          selectedAddons={selectedAddons}
          toggleAddon={toggleAddon}
          handleRemoveLineItem={handleRemoveLineItem}
          setNewLineItem={setNewLineItem}
          setShowLineItemModal={setShowLineItemModal}
          markupSettings={markupSettings}
          setMarkupSettings={setMarkupSettings}
          showMarkupSettings={showMarkupSettings}
          setShowMarkupSettings={setShowMarkupSettings}
          handleCalculate={handleCalculate}
          isNew={isNew}
        />
      )}
    </div>
  );
}
